// navigation.js
import { CLS, openItem, closeItem, openAbout, closeAbout, copyMetaTags, wait } from './navigationHelpers.js'

const nav = document.querySelector('.page-navigation')
const backBtn = document.querySelector('.nav-reset-area')
const aboutLink = document.querySelector('.nav-about-link')
const cache = {}
let navigating = false
let controller = null

const isAboutActive = () => document.body.classList.contains(CLS.aboutOpen)

// Event delegation
document.body.addEventListener('click', async e => {
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
  if (navigating) return

  const navItem = e.target.closest('.nav-item')
  const navAbout = e.target.closest('.nav-about-link')
  const headerImg = e.target.closest('.detail-header-image')
  const articleLink = e.target.closest('article a')

  if (navItem) {
    e.preventDefault()
    navigating = true
    try {
      if (navItem.classList.contains(CLS.active)) {
        await closeItem(navItem)
        history.pushState({}, '', window.php_vars.homeUrl)
      } else {
        // Clear other active states instantly
        nav.querySelector(`.${CLS.active}`)?.classList.remove(CLS.active, CLS.preActive)
        if (isAboutActive()) {
          document.body.classList.remove(CLS.aboutOpen, CLS.aboutPreOpen, CLS.pageActive, CLS.pagePreActive)
        }
        if (await loadPage(navItem.href)) {
          await openItem(navItem)
        }
      }
    } finally {
      navigating = false
    }
    return
  }

  if (navAbout) {
    e.preventDefault()
    navigating = true
    try {
      if (isAboutActive()) {
        await closeAbout()
        history.pushState({}, '', window.php_vars.homeUrl)
      } else {
        // Clear nav item active state instantly
        const active = nav.querySelector(`.${CLS.active}`)
        if (active) {
          active.classList.remove(CLS.active, CLS.preActive)
          document.body.classList.remove(CLS.pageActive, CLS.pagePreActive)
        }
        if (await loadPage(navAbout.href)) {
          await openAbout()
        }
      }
    } finally {
      navigating = false
    }
    return
  }

  if (headerImg) {
    e.preventDefault()
    const parentUrl = headerImg.closest('article')?.dataset.parent
    if (!parentUrl) return

    navigating = true
    try {
      if (isAboutActive()) {
        document.body.classList.remove(CLS.aboutOpen, CLS.aboutPreOpen, CLS.pageActive, CLS.pagePreActive)
      }
      const matchingNavItem = nav.querySelector(`.nav-item[href="${parentUrl}"]`)
      if (await loadPage(parentUrl)) {
        if (matchingNavItem) await openItem(matchingNavItem)
      }
    } finally {
      navigating = false
    }
    return
  }

  if (articleLink && articleLink.origin === location.origin) {
    const href = articleLink.getAttribute('href')
    if (!href || href.startsWith('#') || articleLink.hasAttribute('download') || articleLink.target === '_blank') return

    e.preventDefault()
    navigating = true
    try {
      if (await loadPage(articleLink.href)) {
        syncNavState()
      }
    } finally {
      navigating = false
    }
  }
})

backBtn?.addEventListener('click', async () => {
  if (navigating) return
  navigating = true
  try {
    const active = nav.querySelector(`.${CLS.active}`)
    if (active) {
      await closeItem(active)
    } else if (isAboutActive()) {
      await closeAbout()
    }
    history.pushState({}, '', window.php_vars.homeUrl)
  } finally {
    navigating = false
  }
})

document.body.addEventListener('keydown', async e => {
  if (e.key !== 'Escape' || navigating) return
  const active = nav.querySelector(`.${CLS.active}`)
  if (!active && !isAboutActive()) return

  navigating = true
  try {
    if (active) {
      await closeItem(active)
    } else {
      await closeAbout()
    }
    history.pushState({}, '', window.php_vars.homeUrl)
  } finally {
    navigating = false
  }
})

window.addEventListener('popstate', async () => {
  if (navigating) return
  navigating = true
  try {
    await loadPage(location.href, false)
    syncNavState()
  } finally {
    navigating = false
  }
})

const syncNavState = () => {
  const isHome = location.href === window.php_vars.homeUrl
  const isAboutUrl = aboutLink && aboutLink.href === location.href

  nav.querySelectorAll('.nav-item').forEach(item => {
    const isMatch = item.href === location.href
    item.classList.toggle(CLS.active, isMatch)
    item.classList.toggle(CLS.preActive, isMatch)
  })

  document.body.classList.toggle(CLS.pageActive, !isHome)
  document.body.classList.toggle(CLS.pagePreActive, !isHome)
  document.body.classList.toggle(CLS.aboutOpen, isAboutUrl)
  document.body.classList.toggle(CLS.aboutPreOpen, isAboutUrl)
}

const loadPage = async (url, pushState = true) => {
  if (controller) controller.abort()
  controller = new AbortController()

  let html
  if (cache[url]) {
    html = cache[url]
  } else {
    let res
    try {
      res = await fetch(url, { signal: controller.signal })
    } catch (e) {
      if (e.name === 'AbortError') return false
      throw e
    }
    if (!res.ok) return false
    html = await res.text()
    cache[url] = html
  }

  if (pushState) history.pushState({}, '', url)

  const doc = new DOMParser().parseFromString(html, 'text/html')
  const newPage = doc.querySelector('.single-post')
  if (!newPage) return false

  document.body.classList.add(CLS.transitioning)
  await wait(300)
  document.querySelector('.single-post')?.replaceWith(newPage)
  document.title = doc.title
  window.scrollTo(0, 0)
  window.dispatchEvent(new Event('page:update'))

  copyMetaTags.forEach(name => {
    const selector = `meta[name="${name}"], meta[property="${name}"]`
    const newMeta = doc.querySelector(selector)
    if (!newMeta) return

    let meta = document.querySelector(selector)
    if (!meta) {
      meta = document.createElement('meta')
      meta.setAttribute(newMeta.name ? 'name' : 'property', name)
      document.head.appendChild(meta)
    }
    meta.content = newMeta.content
  })

  document.body.classList.remove(CLS.transitioning)
  return true
}

// Detect about page on initial load
if (aboutLink && aboutLink.href === location.href) {
  document.body.classList.add(CLS.aboutPreOpen, CLS.aboutOpen)
}
